import { updateResearchContext, exposureEventData } from "../utils/researchMeasurement";
/**
 * Analytics Hook
 * ==============
 * Provides comprehensive event tracking and session management for research analytics.
 *
 * Features:
 * - App session management (login → logout/close)
 * - Lesson visit tracking
 * - Event batching and sending
 * - Visibility change detection (pause/resume on tab switch)
 * - Idle detection
 * - Heartbeat for active time tracking
 *
 * Architecture:
 * - useAnalyticsLifecycle(): Singleton hook for timers/listeners (use once in App.tsx)
 * - useAnalyticsActions(): Actions-only hook for tracking (use in any component)
 * - useAnalytics(): Combined hook for backward compatibility
 */

import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import type {
  AnalyticsEventDataMap,
  AnalyticsEventType,
  AnalyticsInternalEventDataMap,
  AnalyticsInternalEventType,
} from "../utils/analyticsEventData";
import { setApiErrorTracker } from "../utils/analyticsFetch";
import {
  enqueueAnalyticsEvents,
  queueAnalyticsRequest,
  flushQueuedAnalyticsEvents,
  postAnalyticsJson,
  syncAnalyticsQueueCount,
} from "../utils/analyticsTransport";
import useCoursesStore from "../stores/coursesStore";
import useUserStore from "../stores/userStore";
import { lessonRevisionContext } from "../utils/contentRevisions";

const HEARTBEAT_INTERVAL_MS = 60000; // 60 seconds
const IDLE_THRESHOLD_MS = 120000; // 120 seconds of no interaction = idle

const getFullRoute = () =>
  `${window.location.pathname}${window.location.search || ""}${window.location.hash || ""}`;

const analyticsStorage = {
  getItem: (name: string) => {
    const userId = useUserStore.getState().user?.id;
    if (!userId) return null;
    return localStorage.getItem(`${name}:${userId}`);
  },
  setItem: (name: string, value: string) => {
    const userId = useUserStore.getState().user?.id;
    if (!userId) return;
    localStorage.setItem(`${name}:${userId}`, value);
  },
  removeItem: (name: string) => {
    const userId = useUserStore.getState().user?.id;
    if (!userId) return;
    localStorage.removeItem(`${name}:${userId}`);
  },
};

const PAGE_VISIT_ROUTE_EXCLUSIONS = new Set([
  "/",
  "/auth/callback",
  "/signin",
  "/signup",
  "/settings",
  "/dashboard",
]);

const shouldTrackRoute = (pathname: string) =>
  !PAGE_VISIT_ROUTE_EXCLUSIONS.has(pathname);

const getScrollDepthPercent = () => {
  if (typeof document === "undefined") return 0;
  const scrollingElement = document.scrollingElement ?? document.documentElement;
  if (!scrollingElement) return 0;
  const { scrollTop, scrollHeight, clientHeight } = scrollingElement;
  if (scrollHeight <= 0 || clientHeight <= 0) return 0;
  if (scrollHeight <= clientHeight) return 100;
  const depth = ((scrollTop + clientHeight) / scrollHeight) * 100;
  return Math.min(100, Math.max(0, Math.round(depth)));
};

// ============================================================================
// Types
// ============================================================================

export interface UserEvent {
  client_event_id?: string;
  event_type: string;
  event_data?: Record<string, unknown>;
  lesson_id?: number;
  unit_id?: number;
  course_id?: number;
  content_revision?: number;
  video_revision?: number;
  quiz_revision?: number;
  coding_revision?: number;
  timestamp?: string;
}

type ContentType = "video" | "quiz" | "coding" | null;

interface AnalyticsState {
  appSessionId: number | null;
  lessonVisitId: number | null;
  isHydrated: boolean;
  isSigningOut: boolean;
  isTabVisible: boolean;
  isIdle: boolean;
  isVideoPlaying: boolean;
  lastInteractionAt: number;
  lastInteractionType: string | null;

  // Cumulative time tracking (in seconds) - app-level
  activeSeconds: number;
  idleSeconds: number;
  awaySeconds: number;
  tabSwitchCount: number;
  pagesVisited: string[];

  // Lesson-specific active time tracking
  lessonActiveSeconds: number;
  lessonActiveStartTime: number | null;

  // Lesson-specific content time tracking
  videoTimeSeconds: number;
  quizTimeSeconds: number;
  codingTimeSeconds: number;
  currentContentType: ContentType;
  contentTypeStartTime: number | null;

  // Actions
  setAppSessionId: (id: number | null) => void;
  setLessonVisitId: (id: number | null) => void;
  setHydrated: (hydrated: boolean) => void;
  setSigningOut: (signingOut: boolean) => void;
  setTabVisible: (visible: boolean) => void;
  setIdle: (idle: boolean) => void;
  setVideoPlaying: (isPlaying: boolean) => void;
  updateLastInteraction: (interactionType?: string | null) => void;
  incrementActiveSeconds: (delta: number) => void;
  incrementIdleSeconds: (delta: number) => void;
  incrementAwaySeconds: (delta: number) => void;
  incrementTabSwitchCount: () => void;
  incrementLessonActiveSeconds: (delta: number) => void;
  addPageVisited: (page: string) => void;
  setCurrentContentType: (type: ContentType) => void;
  flushContentTime: () => void;
  flushLessonActiveTime: () => void;
  resetLessonTimes: () => void;
  resetSession: () => void;
  clearPersistedSession: () => void;
}

// Time semantics: counts only when tab visible + not idle; video requires playback.
// Content time only counts when visible, not idle, and (for video) playing.
const shouldTrackContent = (
  state: Pick<AnalyticsState, "isTabVisible" | "isIdle" | "isVideoPlaying">,
  type: ContentType
) => {
  if (!type) return false;
  if (!state.isTabVisible || state.isIdle) return false;
  if (type === "video") return state.isVideoPlaying;
  return true;
};

// Lesson active time only counts when a lesson visit is active, visible, and not idle.
const shouldTrackLessonActive = (
  state: Pick<AnalyticsState, "isTabVisible" | "isIdle" | "lessonVisitId">
) => {
  return Boolean(state.lessonVisitId) && state.isTabVisible && !state.isIdle;
};

const applyContentTiming = (
  state: AnalyticsState,
  now: number,
  nextType: ContentType = state.currentContentType,
  overrides?: Partial<Pick<AnalyticsState, "isTabVisible" | "isIdle" | "isVideoPlaying">>
) => {
  let videoTimeSeconds = state.videoTimeSeconds;
  let quizTimeSeconds = state.quizTimeSeconds;
  let codingTimeSeconds = state.codingTimeSeconds;

  if (state.currentContentType && state.contentTypeStartTime) {
    const elapsed = Math.floor((now - state.contentTypeStartTime) / 1000);
    if (elapsed > 0) {
      if (state.currentContentType === "video") {
        videoTimeSeconds += elapsed;
      } else if (state.currentContentType === "quiz") {
        quizTimeSeconds += elapsed;
      } else if (state.currentContentType === "coding") {
        codingTimeSeconds += elapsed;
      }
    }
  }

  const trackingState = {
    isTabVisible: overrides?.isTabVisible ?? state.isTabVisible,
    isIdle: overrides?.isIdle ?? state.isIdle,
    isVideoPlaying: overrides?.isVideoPlaying ?? state.isVideoPlaying,
  };

  const shouldTrack = shouldTrackContent(trackingState, nextType);

  return {
    videoTimeSeconds,
    quizTimeSeconds,
    codingTimeSeconds,
    currentContentType: nextType,
    contentTypeStartTime: shouldTrack ? now : null,
  };
};

const applyLessonActiveTiming = (
  state: AnalyticsState,
  now: number,
  overrides?: Partial<Pick<AnalyticsState, "isTabVisible" | "isIdle" | "lessonVisitId">>
) => {
  let lessonActiveSeconds = state.lessonActiveSeconds;

  if (state.lessonActiveStartTime) {
    const elapsed = Math.floor((now - state.lessonActiveStartTime) / 1000);
    if (elapsed > 0) {
      lessonActiveSeconds += elapsed;
    }
  }

  // Null explicitly ends a visit; only an omitted override keeps the current ID.
  const nextLessonVisitId = overrides?.lessonVisitId === undefined
    ? state.lessonVisitId
    : overrides.lessonVisitId;
  const trackingState = {
    isTabVisible: overrides?.isTabVisible ?? state.isTabVisible,
    isIdle: overrides?.isIdle ?? state.isIdle,
    lessonVisitId: nextLessonVisitId,
  };
  const shouldTrack = shouldTrackLessonActive(trackingState);

  return {
    lessonActiveSeconds,
    lessonActiveStartTime: shouldTrack ? now : null,
    lessonVisitId: nextLessonVisitId,
  };
};

// ============================================================================
// Analytics Store (Zustand)
// ============================================================================

export const useAnalyticsStore = create<AnalyticsState>()(
  persist(
    (set) => ({
      appSessionId: null,
      lessonVisitId: null,
      isHydrated: false,
      isSigningOut: false,
      isTabVisible: true,
      isIdle: false,
      isVideoPlaying: false,
      lastInteractionAt: Date.now(),
      lastInteractionType: null,

      activeSeconds: 0,
      idleSeconds: 0,
      awaySeconds: 0,
      tabSwitchCount: 0,
      pagesVisited: [],

      lessonActiveSeconds: 0,
      lessonActiveStartTime: null,

      videoTimeSeconds: 0,
      quizTimeSeconds: 0,
      codingTimeSeconds: 0,
      currentContentType: null,
      contentTypeStartTime: null,

      setAppSessionId: (id) => set({ appSessionId: id }),
      setLessonVisitId: (id) =>
        set((state) => applyLessonActiveTiming(state, Date.now(), { lessonVisitId: id })),
      setHydrated: (hydrated) => set({ isHydrated: hydrated }),
      setSigningOut: (signingOut) => set({ isSigningOut: signingOut }),
      setTabVisible: (visible) =>
        set((state) => {
          const now = Date.now();
          const lessonUpdates = applyLessonActiveTiming(state, now, { isTabVisible: visible });
          const contentUpdates = applyContentTiming(state, now, state.currentContentType, {
            isTabVisible: visible,
          });
          return { ...lessonUpdates, ...contentUpdates, isTabVisible: visible };
        }),
      setIdle: (idle) =>
        set((state) => {
          const now = Date.now();
          const lessonUpdates = applyLessonActiveTiming(state, now, { isIdle: idle });
          const contentUpdates = applyContentTiming(state, now, state.currentContentType, {
            isIdle: idle,
          });
          return { ...lessonUpdates, ...contentUpdates, isIdle: idle };
        }),
      setVideoPlaying: (isPlaying) =>
        set((state) => {
          const now = Date.now();
          const contentUpdates = applyContentTiming(state, now, state.currentContentType, {
            isVideoPlaying: isPlaying,
          });
          return { ...contentUpdates, isVideoPlaying: isPlaying };
        }),
      updateLastInteraction: (interactionType) =>
        set((state) => {
          const now = Date.now();
          const nextInteractionType =
            interactionType !== undefined ? interactionType : state.lastInteractionType;
          if (!state.isIdle) {
            return {
              lastInteractionAt: now,
              lastInteractionType: nextInteractionType,
            };
          }
          const lessonUpdates = applyLessonActiveTiming(state, now, { isIdle: false });
          const contentUpdates = applyContentTiming(state, now, state.currentContentType, {
            isIdle: false,
          });
          return {
            ...lessonUpdates,
            ...contentUpdates,
            lastInteractionAt: now,
            lastInteractionType: nextInteractionType,
            isIdle: false,
          };
        }),

      incrementActiveSeconds: (delta) =>
        set((state) => ({ activeSeconds: state.activeSeconds + delta })),
      incrementIdleSeconds: (delta) =>
        set((state) => ({ idleSeconds: state.idleSeconds + delta })),
      incrementAwaySeconds: (delta) =>
        set((state) => ({ awaySeconds: state.awaySeconds + delta })),
      incrementTabSwitchCount: () =>
        set((state) => ({ tabSwitchCount: state.tabSwitchCount + 1 })),
      incrementLessonActiveSeconds: (delta) =>
        set((state) => ({ lessonActiveSeconds: state.lessonActiveSeconds + delta })),
      addPageVisited: (page) =>
        set((state) => {
          const pages = state.pagesVisited;
          if (pages.length === 0 || pages[pages.length - 1] !== page) {
            return { pagesVisited: [...pages, page] };
          }
          return state;
        }),

      setCurrentContentType: (type) =>
        set((state) => applyContentTiming(state, Date.now(), type)),

      flushContentTime: () => set((state) => applyContentTiming(state, Date.now())),
      flushLessonActiveTime: () => set((state) => applyLessonActiveTiming(state, Date.now())),

      resetLessonTimes: () =>
        set({
          lessonActiveSeconds: 0,
          lessonActiveStartTime: null,
          videoTimeSeconds: 0,
          quizTimeSeconds: 0,
          codingTimeSeconds: 0,
          currentContentType: null,
          contentTypeStartTime: null,
        }),

      resetSession: () =>
        set({
          appSessionId: null,
          lessonVisitId: null,
          isVideoPlaying: false,
          lastInteractionType: null,
          activeSeconds: 0,
          idleSeconds: 0,
          awaySeconds: 0,
          tabSwitchCount: 0,
          pagesVisited: [],
          lessonActiveSeconds: 0,
          lessonActiveStartTime: null,
          videoTimeSeconds: 0,
          quizTimeSeconds: 0,
          codingTimeSeconds: 0,
          currentContentType: null,
          contentTypeStartTime: null,
        }),

      clearPersistedSession: () =>
        set({
          appSessionId: null,
          lessonVisitId: null,
          lastInteractionType: null,
          // Also reset counters that may have been persisted by older versions
          activeSeconds: 0,
          idleSeconds: 0,
          awaySeconds: 0,
          tabSwitchCount: 0,
          pagesVisited: [],
          lessonActiveSeconds: 0,
          lessonActiveStartTime: null,
        }),
    }),
    {
      name: "faheem-analytics",
      storage: createJSONStorage(() => analyticsStorage),
      onRehydrateStorage: () => (state, error) => {
        if (error && process.env.NODE_ENV === "development") {
          console.warn("[analytics] Failed to rehydrate session state:", error);
        }
        state?.setHydrated(true);
      },
      // Only persist session IDs, not cumulative counters (prevents stale carry-over)
      partialize: (state) => ({
        appSessionId: state.appSessionId,
        lessonVisitId: state.lessonVisitId,
      }),
    }
  )
);

useAnalyticsStore.subscribe((state) => updateResearchContext({
  appSessionId: state.appSessionId,
  lessonVisitId: state.lessonVisitId,
  active: state.isTabVisible && !state.isIdle,
}));

// ============================================================================
// API Functions
// ============================================================================

async function apiCall<T>(
  endpoint: string,
  data: Record<string, unknown>,
  useKeepalive = false
): Promise<T | null> {
  if (["/telemetry/app-session/heartbeat", "/telemetry/app-session/end",
       "/telemetry/lesson-visit/heartbeat", "/telemetry/lesson-visit/end",
       "/telemetry/quiz-question-view"].includes(endpoint)) {
    await queueAnalyticsRequest(endpoint, data, useKeepalive);
    return null;
  }
  const result = await postAnalyticsJson<T>(endpoint, data, {
    keepalive: useKeepalive,
  });
  return result.ok ? result.data : null;
}

async function sendAnalyticsEvents(
  data: Record<string, unknown>,
  useKeepalive = false
): Promise<unknown | null> {
  const studentId = data.student_id;
  const events = data.events;
  if (typeof studentId !== "string" || !Array.isArray(events)) return null;

  enqueueAnalyticsEvents(
    studentId,
    typeof data.app_session_id === "number" ? data.app_session_id : null,
    typeof data.lesson_visit_id === "number" ? data.lesson_visit_id : null,
    (events as UserEvent[]).map((event) => {
      const loadedLesson = event.lesson_id
        ? useCoursesStore.getState().lessonsContent[event.lesson_id]?.data
        : undefined;
      return {
        ...lessonRevisionContext(loadedLesson),
        ...event,
        event_data: exposureEventData(event.event_data),
        timestamp: event.timestamp ?? new Date().toISOString(),
      };
    })
  );

  const currentAppSessionId = useAnalyticsStore.getState().appSessionId;
  return flushQueuedAnalyticsEvents(studentId, currentAppSessionId, {
    keepalive: useKeepalive,
  });
}

export function trackEventOutsideReact<T extends AnalyticsEventType>(
  eventType: T,
  eventData?: AnalyticsEventDataMap[T],
  options?: {
    lessonId?: number;
    unitId?: number;
    courseId?: number;
    studentId?: string;
  }
): void {
  const studentId = options?.studentId ?? useUserStore.getState().user?.id;
  if (!studentId) return;

  const currentStore = useAnalyticsStore.getState();
  const events: UserEvent[] = [
    {
      event_type: eventType,
      event_data: eventData,
      lesson_id: options?.lessonId,
      unit_id: options?.unitId,
      course_id: options?.courseId,
      timestamp: new Date().toISOString(),
    },
  ];

  sendAnalyticsEvents({
    student_id: studentId,
    app_session_id: currentStore.appSessionId,
    lesson_visit_id: currentStore.lessonVisitId,
    events,
  });

  currentStore.updateLastInteraction();
}

setApiErrorTracker(trackEventOutsideReact);

// ============================================================================
// Singleton Guard for Lifecycle Hook
// ============================================================================

let lifecycleInitialized = false;

// ============================================================================
// Analytics Lifecycle Hook (Singleton - use ONCE in App.tsx)
// ============================================================================

/**
 * useAnalyticsLifecycle
 * Manages all timers, intervals, and event listeners for analytics.
 * MUST only be called once in the entire app (in App.tsx/AnalyticsSessionManager).
 *
 * Handles:
 * - Heartbeat interval
 * - Idle detection interval
 * - User interaction listeners
 * - Visibility change listener
 * - page lifecycle listeners
 * - Page tracking
 */
export function useAnalyticsLifecycle(studentId: string | undefined) {
  const store = useAnalyticsStore();
  const location = useLocation();
  const appSessionId = useAnalyticsStore((state) => state.appSessionId);
  const lessonVisitId = useAnalyticsStore((state) => state.lessonVisitId);
  const isHydrated = useAnalyticsStore((state) => state.isHydrated);
  const analyticsDebugEnabled = import.meta.env.VITE_ANALYTICS_DEBUG === "true";
  const heartbeatIntervalRef = useRef<number | null>(null);
  const idleCheckIntervalRef = useRef<number | null>(null);
  const lastHeartbeatRef = useRef<number>(Date.now());
  const tabHiddenAtRef = useRef<number | null>(null);
  const awayAccumulatedSinceHiddenRef = useRef<number>(0);
  const idleStartedAtRef = useRef<number | null>(null);
  const maxScrollDepthPercentRef = useRef<number>(0);
  const initializedRef = useRef(false);
  // Owner guard: only the instance that initialized can register listeners/intervals
  const isOwnerRef = useRef(false);
  const exitFlushSentRef = useRef(false);

  // ----------------------------------------
  // Heartbeat
  // ----------------------------------------

  const sendHeartbeat = useCallback((useKeepalive = false) => {
    const currentStore = useAnalyticsStore.getState();
    if (!currentStore.appSessionId || !studentId) return;

    const intervalStart = lastHeartbeatRef.current;
    const intervalEnd = Date.now();
    const intervalSeconds = Math.floor((intervalEnd - intervalStart) / 1000);
    lastHeartbeatRef.current = intervalEnd;

    if (intervalSeconds <= 0) {
      return;
    }

    // Determine how to split the time
    let activeDelta = 0;
    let idleDelta = 0;
    let awayDelta = 0;

    if (!currentStore.isTabVisible) {
      const hiddenSince = tabHiddenAtRef.current ?? intervalStart;
      const hiddenStart = Math.max(hiddenSince, intervalStart);
      awayDelta = Math.max(0, Math.floor((intervalEnd - hiddenStart) / 1000));
      awayAccumulatedSinceHiddenRef.current += awayDelta;
      activeDelta = Math.max(0, intervalSeconds - awayDelta);
    } else if (currentStore.isIdle) {
      const idleSince = idleStartedAtRef.current ?? intervalStart;
      const idleStart = Math.max(idleSince, intervalStart);
      idleDelta = Math.max(0, Math.floor((intervalEnd - idleStart) / 1000));
      activeDelta = Math.max(0, intervalSeconds - idleDelta);
    } else {
      activeDelta = intervalSeconds;
    }

    if (activeDelta > 0) currentStore.incrementActiveSeconds(activeDelta);
    if (idleDelta > 0) currentStore.incrementIdleSeconds(idleDelta);
    if (awayDelta > 0) currentStore.incrementAwaySeconds(awayDelta);

    void apiCall(
      "/telemetry/app-session/heartbeat",
      {
        app_session_id: currentStore.appSessionId,
        student_id: studentId,
        total_active_seconds: currentStore.activeSeconds + activeDelta,
        total_idle_seconds: currentStore.idleSeconds + idleDelta,
        total_away_seconds: currentStore.awaySeconds + awayDelta,
        tab_switch_count: currentStore.tabSwitchCount,
        current_page: getFullRoute(),
      },
      useKeepalive
    );

    // Flush and send lesson-visit counters periodically
    currentStore.flushContentTime();
    currentStore.flushLessonActiveTime();
    const updatedStore = useAnalyticsStore.getState();
    if (updatedStore.lessonVisitId) {
      if (analyticsDebugEnabled) {
        console.debug("[analytics] lesson-visit heartbeat", {
          lessonVisitId: updatedStore.lessonVisitId,
          activeSeconds: updatedStore.lessonActiveSeconds,
          videoTimeSeconds: updatedStore.videoTimeSeconds,
          quizTimeSeconds: updatedStore.quizTimeSeconds,
          codingTimeSeconds: updatedStore.codingTimeSeconds,
        });
      }
      void apiCall(
        "/telemetry/lesson-visit/heartbeat",
        {
          lesson_visit_id: updatedStore.lessonVisitId,
          student_id: studentId,
          active_seconds: updatedStore.lessonActiveSeconds,
          video_time_seconds: updatedStore.videoTimeSeconds,
          quiz_time_seconds: updatedStore.quizTimeSeconds,
          coding_time_seconds: updatedStore.codingTimeSeconds,
        },
        useKeepalive
      );
    }

    void flushQueuedAnalyticsEvents(studentId, currentStore.appSessionId, {
      keepalive: useKeepalive,
    });
  }, [studentId, analyticsDebugEnabled]);

  // ----------------------------------------
  // Track Event (for visibility change tracking)
  // ----------------------------------------

  const trackEventInternal = useCallback(
    <T extends AnalyticsInternalEventType>(
      eventType: T,
      eventData?: AnalyticsInternalEventDataMap[T],
      options?: { markInteraction?: boolean }
    ) => {
      const currentStore = useAnalyticsStore.getState();
      if (!studentId) return;
      const { markInteraction = true } = options ?? {};

      const events: UserEvent[] = [
        {
          event_type: eventType,
          event_data: eventData,
          timestamp: new Date().toISOString(),
        },
      ];

      sendAnalyticsEvents({
        student_id: studentId,
        app_session_id: currentStore.appSessionId,
        lesson_visit_id: currentStore.lessonVisitId,
        events,
      });

      if (markInteraction) {
        currentStore.updateLastInteraction();
      }
    },
    [studentId]
  );

  const updateMaxScrollDepth = useCallback(() => {
    if (!shouldTrackRoute(window.location.pathname)) {
      return;
    }
    const depth = getScrollDepthPercent();
    if (depth > maxScrollDepthPercentRef.current) {
      maxScrollDepthPercentRef.current = depth;
    }
  }, []);

  const resetMaxScrollDepth = useCallback(() => {
    maxScrollDepthPercentRef.current = getScrollDepthPercent();
  }, []);

  type PageViewQueueEntry = {
    route: string;
    referrerRoute: string | null;
    occurredAt: string;
    sequence: number;
    courseId?: number | null;
    unitId?: number | null;
    lessonId?: number | null;
  };

  const lastTrackedRouteRef = useRef<string | null>(null);
  const lastTrackedRouteAtRef = useRef<number | null>(null);
  const pageViewSequenceRef = useRef(0);
  const pageViewQueueRef = useRef<PageViewQueueEntry[]>([]);
  const pageViewFlushInFlightRef = useRef<Promise<void> | null>(null);

  const getPageViewContext = useCallback((pathname: string) => {
    if (pathname !== "/dashboard/learn") {
      return { courseId: null, unitId: null, lessonId: null };
    }
    const { currentCourseId, currentUnitId, currentLessonId } =
      useCoursesStore.getState();
    return {
      courseId: currentCourseId ?? null,
      unitId: currentUnitId ?? null,
      lessonId: currentLessonId ?? null,
    };
  }, []);

  const buildPageViewEventData = useCallback(
    (entry: PageViewQueueEntry): AnalyticsInternalEventDataMap["page_view"] => {
      const eventData: AnalyticsInternalEventDataMap["page_view"] = {
        route: entry.route,
        referrer_route: entry.referrerRoute,
        occurred_at: entry.occurredAt,
        sequence: entry.sequence,
      };

      if (typeof entry.courseId === "number") {
        eventData.course_id = entry.courseId;
      }
      if (typeof entry.unitId === "number") {
        eventData.unit_id = entry.unitId;
      }
      if (typeof entry.lessonId === "number") {
        eventData.lesson_id = entry.lessonId;
      }

      return eventData;
    },
    []
  );

  const flushPageViewQueue = useCallback(async () => {
    const initialStore = useAnalyticsStore.getState();
    if (!initialStore.appSessionId || !studentId) return;

    if (pageViewFlushInFlightRef.current) {
      return pageViewFlushInFlightRef.current;
    }

    const flushPromise = (async () => {
      while (pageViewQueueRef.current.length > 0) {
        const storeSnapshot = useAnalyticsStore.getState();
        if (!storeSnapshot.appSessionId || !studentId) {
          return;
        }

        const batch = pageViewQueueRef.current.splice(
          0,
          pageViewQueueRef.current.length
        );

        if (batch.length === 0) {
          return;
        }

        if (analyticsDebugEnabled) {
          console.debug("[analytics] flush page_view batch", {
            count: batch.length,
            routes: batch.map((entry) => entry.route),
            sequences: batch.map((entry) => entry.sequence),
          });
        }

        const events: UserEvent[] = batch.map((entry) => ({
          event_type: "page_view",
          event_data: buildPageViewEventData(entry),
          timestamp: entry.occurredAt,
        }));

        await sendAnalyticsEvents({
          student_id: studentId,
          app_session_id: storeSnapshot.appSessionId,
          lesson_visit_id: storeSnapshot.lessonVisitId,
          events,
        });

        storeSnapshot.updateLastInteraction();
      }
    })();

    pageViewFlushInFlightRef.current = flushPromise;

    try {
      await flushPromise;
    } finally {
      if (pageViewFlushInFlightRef.current === flushPromise) {
        pageViewFlushInFlightRef.current = null;
      }
      if (pageViewQueueRef.current.length > 0) {
        void flushPageViewQueue();
      }
    }

    return flushPromise;
  }, [studentId, analyticsDebugEnabled, buildPageViewEventData]);

  const enqueuePageView = useCallback(
    (route: string, pathname: string, referrerRoute: string | null) => {
      if (!studentId) return;

      const sequence = pageViewSequenceRef.current + 1;
      pageViewSequenceRef.current = sequence;
      const occurredAt = new Date().toISOString();
      const context = getPageViewContext(pathname);

      pageViewQueueRef.current.push({
        route,
        referrerRoute,
        occurredAt,
        sequence,
        courseId: context.courseId,
        unitId: context.unitId,
        lessonId: context.lessonId,
      });

      if (analyticsDebugEnabled) {
        console.debug("[analytics] queued page_view", {
          route,
          referrerRoute,
          sequence,
          context,
        });
      }

      const currentStore = useAnalyticsStore.getState();
      currentStore.updateLastInteraction();
      void flushPageViewQueue();
    },
    [studentId, analyticsDebugEnabled, flushPageViewQueue, getPageViewContext]
  );

  const sendPageLeaveBeacon = useCallback(() => {
    if (exitFlushSentRef.current) return;
    if (!studentId) return;
    const currentStore = useAnalyticsStore.getState();
    const lastRoute = lastTrackedRouteRef.current;
    const lastRouteAt = lastTrackedRouteAtRef.current;
    if (!lastRoute || !lastRouteAt) return;

    const timeOnPageSeconds = Math.max(
      0,
      Math.floor((Date.now() - lastRouteAt) / 1000)
    );
    const scrollDepthPercent = Math.max(
      maxScrollDepthPercentRef.current,
      getScrollDepthPercent()
    );

    const eventData: AnalyticsInternalEventDataMap["page_leave"] = {
      route: lastRoute,
      time_on_page_seconds: timeOnPageSeconds,
    };

    if (Number.isFinite(scrollDepthPercent)) {
      eventData.scroll_depth_percent = scrollDepthPercent;
    }

    exitFlushSentRef.current = true;
    void sendAnalyticsEvents(
      {
        student_id: studentId,
        app_session_id: currentStore.appSessionId,
        lesson_visit_id: currentStore.lessonVisitId,
        events: [
          {
            event_type: "page_leave",
            event_data: eventData,
            timestamp: new Date().toISOString(),
          },
        ],
      },
      true
    );
  }, [studentId]);

  // ----------------------------------------
  // Initialize lifecycle (only once)
  // ----------------------------------------

  useEffect(() => {
    // Guard against double initialization (React 18 StrictMode mounts twice)
    if (initializedRef.current || lifecycleInitialized) {
      if (process.env.NODE_ENV === "development") {
        console.warn(
          "[useAnalyticsLifecycle] Duplicate mount detected. Skipping initialization."
        );
      }
      return;
    }

    if (!studentId) return;

    initializedRef.current = true;
    lifecycleInitialized = true;
    isOwnerRef.current = true; // Mark this instance as the owner

    const startsVisible = document.visibilityState === "visible";
    useAnalyticsStore.getState().setTabVisible(startsVisible);
    tabHiddenAtRef.current = startsVisible ? null : Date.now();

    return () => {
      initializedRef.current = false;
      lifecycleInitialized = false;
      isOwnerRef.current = false; // Relinquish ownership on cleanup
    };
  }, [studentId]);

  // ----------------------------------------
  // Validate persisted session IDs
  // ----------------------------------------

  useEffect(() => {
    if (!studentId || !isHydrated) return;
    if (!appSessionId && !lessonVisitId) return;

    // Capture validated IDs before async call to guard against stale responses
    const validatedAppSessionId = appSessionId;
    const validatedLessonVisitId = lessonVisitId;

    apiCall<{ app_session_valid: boolean; lesson_visit_valid: boolean }>(
      "/telemetry/app-session/status",
      {
        student_id: studentId,
        app_session_id: validatedAppSessionId,
        lesson_visit_id: validatedLessonVisitId,
      }
    ).then((result) => {
      if (!result) {
        if (process.env.NODE_ENV === "development") {
          console.warn(
            "[useAnalyticsLifecycle] Session validation failed (network/server error). Keeping persisted IDs; will retry on next startup."
          );
        }
        return;
      }

      const nowStore = useAnalyticsStore.getState();

      if (validatedAppSessionId && !result.app_session_valid) {
        if (nowStore.appSessionId === validatedAppSessionId) {
          nowStore.resetSession();
          return;
        } else if (process.env.NODE_ENV === "development") {
          console.warn(
            "[useAnalyticsLifecycle] Ignoring stale validation for appSessionId:",
            validatedAppSessionId,
            "- current is",
            nowStore.appSessionId
          );
        }
      }

      if (validatedLessonVisitId && !result.lesson_visit_valid) {
        if (nowStore.lessonVisitId === validatedLessonVisitId) {
          nowStore.setLessonVisitId(null);
          nowStore.resetLessonTimes();
        } else if (process.env.NODE_ENV === "development") {
          console.warn(
            "[useAnalyticsLifecycle] Ignoring stale validation for lessonVisitId:",
            validatedLessonVisitId,
            "- current is",
            nowStore.lessonVisitId
          );
        }
      }

    });
  }, [studentId, isHydrated, appSessionId, lessonVisitId]);

  // ----------------------------------------
  // Visibility & Idle Detection
  // ----------------------------------------

  useEffect(() => {
    // Guard: only owner instance sets up listener
    if (!studentId || !lifecycleInitialized || !isOwnerRef.current) return;

    const handleVisibilityChange = () => {
      const currentStore = useAnalyticsStore.getState();
      const isVisible = document.visibilityState === "visible";

      if (!isVisible) {
        // Tab became hidden
        tabHiddenAtRef.current = Date.now();
        awayAccumulatedSinceHiddenRef.current = 0;
        currentStore.setTabVisible(false);
        currentStore.incrementTabSwitchCount();

        trackEventInternal(
          "tab_blur",
          {
            current_content_type: currentStore.currentContentType,
          },
          { markInteraction: false }
        );
      } else {
        exitFlushSentRef.current = false;
        // Tab became visible again
        const totalHiddenSeconds = tabHiddenAtRef.current
          ? Math.floor((Date.now() - tabHiddenAtRef.current) / 1000)
          : 0;
        const alreadyCounted = awayAccumulatedSinceHiddenRef.current;
        const missingAwaySeconds = Math.max(0, totalHiddenSeconds - alreadyCounted);

        tabHiddenAtRef.current = null;
        awayAccumulatedSinceHiddenRef.current = 0;
        currentStore.setTabVisible(true);

        trackEventInternal(
          "tab_focus",
          {
            away_duration_seconds: totalHiddenSeconds,
          },
          { markInteraction: false }
        );

        if (missingAwaySeconds > 0) {
          currentStore.incrementAwaySeconds(missingAwaySeconds);
        }

        if (currentStore.appSessionId && studentId) {
          void apiCall("/telemetry/app-session/heartbeat", {
            app_session_id: currentStore.appSessionId,
            student_id: studentId,
            total_active_seconds: useAnalyticsStore.getState().activeSeconds,
            total_idle_seconds: useAnalyticsStore.getState().idleSeconds,
            total_away_seconds: useAnalyticsStore.getState().awaySeconds,
            tab_switch_count: currentStore.tabSwitchCount,
            current_page: getFullRoute(),
          });
          void flushQueuedAnalyticsEvents(studentId, currentStore.appSessionId);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [studentId, trackEventInternal]);

  useEffect(() => {
    if (!studentId || !appSessionId) return;

    syncAnalyticsQueueCount(studentId);
    void flushQueuedAnalyticsEvents(studentId, appSessionId);

    const flush = () => {
      const currentSessionId = useAnalyticsStore.getState().appSessionId;
      if (currentSessionId) {
        void flushQueuedAnalyticsEvents(studentId, currentSessionId);
      }
    };
    const handlePageShow = () => {
      exitFlushSentRef.current = false;
      flush();
    };

    window.addEventListener("online", flush);
    window.addEventListener("pageshow", handlePageShow);
    return () => {
      window.removeEventListener("online", flush);
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, [studentId, appSessionId]);

  // Idle detection
  useEffect(() => {
    // Guard: only owner instance sets up interval
    if (!studentId || !lifecycleInitialized || !isOwnerRef.current) return;

    const checkIdle = () => {
      const currentStore = useAnalyticsStore.getState();
      if (currentStore.isVideoPlaying && currentStore.isTabVisible) {
        return;
      }
      const now = Date.now();
      const timeSinceLastInteraction = now - currentStore.lastInteractionAt;

      if (
        timeSinceLastInteraction >= IDLE_THRESHOLD_MS &&
        !currentStore.isIdle &&
        currentStore.isTabVisible
      ) {
        currentStore.setIdle(true);
        idleStartedAtRef.current = Date.now();
        trackEventInternal(
          "idle_start",
          {
            last_interaction_type: currentStore.lastInteractionType ?? "unknown",
          },
          { markInteraction: false }
        );
      }
    };

    idleCheckIntervalRef.current = window.setInterval(checkIdle, 10000); // Check every 10 seconds

    return () => {
      if (idleCheckIntervalRef.current) {
        clearInterval(idleCheckIntervalRef.current);
      }
    };
  }, [studentId, trackEventInternal]);

  // Track user interactions to reset idle
  useEffect(() => {
    // Guard: only owner instance sets up listeners
    if (!studentId || !lifecycleInitialized || !isOwnerRef.current) return;

    const handleInteraction = (event: Event) => {
      const currentStore = useAnalyticsStore.getState();
      if (event.type === "scroll") {
        updateMaxScrollDepth();
      }
      if (currentStore.isIdle) {
        const idleStartedAt = idleStartedAtRef.current ?? currentStore.lastInteractionAt;
        const idleDuration = Math.max(0, Math.floor((Date.now() - idleStartedAt) / 1000));
        trackEventInternal(
          "idle_end",
          { idle_duration_seconds: idleDuration },
          { markInteraction: false }
        );
      }
      idleStartedAtRef.current = null;
      currentStore.updateLastInteraction(event.type);
    };

    const events = ["mousedown", "keydown", "scroll", "touchstart"];
    events.forEach((event) => {
      window.addEventListener(event, handleInteraction, { passive: true });
    });

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleInteraction);
      });
    };
  }, [studentId, trackEventInternal, updateMaxScrollDepth]);

  // ----------------------------------------
  // Heartbeat
  // ----------------------------------------

  useEffect(() => {
    // Guard: only owner instance sets up interval
    if (!store.appSessionId || !lifecycleInitialized || !isOwnerRef.current) return;

    // Reset heartbeat timer when session starts (Step 6)
    lastHeartbeatRef.current = Date.now();

    heartbeatIntervalRef.current = window.setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);

    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
    };
  }, [store.appSessionId, sendHeartbeat]);

  // ----------------------------------------
  // Cleanup on unmount/close
  // ----------------------------------------

  useEffect(() => {
    // Guard: only owner instance sets up listener
    if (!lifecycleInitialized || !isOwnerRef.current) return;

    const handleExit = () => {
      sendPageLeaveBeacon();
      sendHeartbeat(true);
    };

    window.addEventListener("pagehide", handleExit);

    return () => {
      window.removeEventListener("pagehide", handleExit);
    };
  }, [sendPageLeaveBeacon, sendHeartbeat]);

  // ----------------------------------------
  // Page tracking (route change)
  // ----------------------------------------

  useEffect(() => {
    if (!lifecycleInitialized || !isOwnerRef.current || !studentId) return;
    const currentStore = useAnalyticsStore.getState();

    const pathname = location.pathname;
    const route = `${location.pathname}${location.search || ""}${location.hash || ""}`;
    const now = Date.now();
    const previousRoute = lastTrackedRouteRef.current;
    const previousRouteAt = lastTrackedRouteAtRef.current;
    const isTrackable = shouldTrackRoute(pathname);

    if (previousRoute && previousRouteAt && previousRoute !== route) {
      const timeOnPageSeconds = Math.max(
        0,
        Math.floor((now - previousRouteAt) / 1000)
      );

      if (currentStore.appSessionId) {
        const eventData: AnalyticsInternalEventDataMap["page_leave"] = {
          route: previousRoute,
          time_on_page_seconds: timeOnPageSeconds,
        };
        if (Number.isFinite(maxScrollDepthPercentRef.current)) {
          eventData.scroll_depth_percent = maxScrollDepthPercentRef.current;
        }
        trackEventInternal("page_leave", eventData);
      }

      if (!isTrackable) {
        lastTrackedRouteAtRef.current = null;
      }
    }

    if (!isTrackable) {
      return;
    }

    if (lastTrackedRouteRef.current === route && lastTrackedRouteAtRef.current) {
      return;
    }

    enqueuePageView(route, pathname, previousRoute);

    lastTrackedRouteRef.current = route;
    lastTrackedRouteAtRef.current = now;
    resetMaxScrollDepth();
  }, [
    location.pathname,
    location.search,
    location.hash,
    studentId,
    trackEventInternal,
    enqueuePageView,
    resetMaxScrollDepth,
  ]);

  // Emit initial page_view when a session starts (even without route change)
  useEffect(() => {
    if (!lifecycleInitialized || !isOwnerRef.current || !studentId || !appSessionId) return;

    if (pageViewQueueRef.current.length > 0) {
      void flushPageViewQueue();
      return;
    }

    const route = getFullRoute();
    const pathname = window.location.pathname;
    if (!shouldTrackRoute(pathname)) {
      return;
    }

    if (lastTrackedRouteRef.current === route && lastTrackedRouteAtRef.current) {
      return;
    }

    enqueuePageView(route, pathname, lastTrackedRouteRef.current);
    lastTrackedRouteRef.current = route;
    lastTrackedRouteAtRef.current = Date.now();
    resetMaxScrollDepth();

  }, [appSessionId, studentId, enqueuePageView, flushPageViewQueue, resetMaxScrollDepth]);
}

// ============================================================================
// Analytics Actions Hook (use in any component)
// ============================================================================

/**
 * useAnalyticsActions
 * Provides action functions for tracking events and managing sessions.
 * Can be called from any component.
 */
export function useAnalyticsActions(studentId: string | undefined) {
  const appSessionId = useAnalyticsStore((state) => state.appSessionId);
  const lessonVisitId = useAnalyticsStore((state) => state.lessonVisitId);
  const isHydrated = useAnalyticsStore((state) => state.isHydrated);
  const isSigningOut = useAnalyticsStore((state) => state.isSigningOut);
  const isTabVisible = useAnalyticsStore((state) => state.isTabVisible);
  const isIdle = useAnalyticsStore((state) => state.isIdle);
  const setAppSessionId = useAnalyticsStore((state) => state.setAppSessionId);
  const setLessonVisitId = useAnalyticsStore((state) => state.setLessonVisitId);
  const resetSession = useAnalyticsStore((state) => state.resetSession);
  const resetLessonTimes = useAnalyticsStore((state) => state.resetLessonTimes);
  const setCurrentContentType = useAnalyticsStore((state) => state.setCurrentContentType);
  const setVideoPlaying = useAnalyticsStore((state) => state.setVideoPlaying);
  const lastHeartbeatRef = useRef<number>(Date.now());
  const startInFlightRef = useRef(false);
  const analyticsDebugEnabled = import.meta.env.VITE_ANALYTICS_DEBUG === "true";

  // ----------------------------------------
  // App Session Management
  // ----------------------------------------

  const startAppSession = useCallback(async () => {
    const currentAppSessionId = useAnalyticsStore.getState().appSessionId;
    if (currentAppSessionId) return currentAppSessionId;
    if (!studentId || startInFlightRef.current) return null;
    if (!isHydrated || isSigningOut) return null;
    startInFlightRef.current = true;

    try {
      const result = await apiCall<{ app_session_id: number }>("/telemetry/app-session/start", {
        student_id: studentId,
        user_agent: navigator.userAgent,
        screen_width: window.screen.width,
        screen_height: window.screen.height,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });

      if (result?.app_session_id) {
        setAppSessionId(result.app_session_id);
        // Reset heartbeat timer on session start (Step 6)
        lastHeartbeatRef.current = Date.now();
        void flushQueuedAnalyticsEvents(studentId, result.app_session_id);
        return result.app_session_id;
      }
      return null;
    } finally {
      startInFlightRef.current = false;
    }
  }, [studentId, isHydrated, isSigningOut, setAppSessionId]);

  const endAppSession = useCallback(
    async (useKeepalive = false) => {
      if (!appSessionId || !studentId) return;
      const closingAppSessionId = appSessionId;
      const pendingRequests: Promise<unknown | null>[] = [];

      // Flush any pending content time
      const currentStore = useAnalyticsStore.getState();
      currentStore.flushContentTime();
      currentStore.flushLessonActiveTime();

      const updatedStore = useAnalyticsStore.getState();
      if (updatedStore.lessonVisitId) {
        const lessonEndPromise = apiCall(
          "/telemetry/lesson-visit/end",
          {
            lesson_visit_id: updatedStore.lessonVisitId,
            student_id: studentId,
            active_seconds: updatedStore.lessonActiveSeconds,
            video_time_seconds: updatedStore.videoTimeSeconds,
            quiz_time_seconds: updatedStore.quizTimeSeconds,
            coding_time_seconds: updatedStore.codingTimeSeconds,
          },
          useKeepalive
        );
        if (!useKeepalive) {
          pendingRequests.push(lessonEndPromise);
        }
      }

      // Don't send pages_visited - let server heartbeats be source of truth (Step 4)
      const appEndPromise = apiCall(
        "/telemetry/app-session/end",
        {
          app_session_id: appSessionId,
          student_id: studentId,
          total_active_seconds: updatedStore.activeSeconds,
          total_idle_seconds: updatedStore.idleSeconds,
          total_away_seconds: updatedStore.awaySeconds,
          tab_switch_count: updatedStore.tabSwitchCount,
          // pages_visited intentionally omitted
        },
        useKeepalive
      );
      if (!useKeepalive) {
        pendingRequests.push(appEndPromise);
      }

      if (useKeepalive) {
        resetSession();
        return;
      }

      await flushQueuedAnalyticsEvents(studentId, appSessionId);
      await Promise.allSettled(pendingRequests);
      const latestStore = useAnalyticsStore.getState();
      if (latestStore.appSessionId === closingAppSessionId) {
        latestStore.resetSession();
      }
    },
    [
      appSessionId,
      studentId,
      resetSession,
    ]
  );

  // ----------------------------------------
  // Lesson Visit Management
  // ----------------------------------------

  const startLessonVisit = useCallback(
    async (
      lessonId: number,
      unitId?: number,
      courseId?: number
    ): Promise<number | null> => {
      if (!studentId || !isHydrated) return null;

      // The page starts visit tracking while lesson content is still loading.
      // Wait for its actual revisions; the page's existing retry will resume.
      const loadedLesson = useCoursesStore.getState().lessonsContent[lessonId]?.data;
      if (!loadedLesson) return null;
      const revisions = lessonRevisionContext(loadedLesson);

      const currentStore = useAnalyticsStore.getState();
      if (analyticsDebugEnabled) {
        console.debug("[analytics] startLessonVisit", {
          lessonId,
          unitId,
          courseId,
          appSessionId: currentStore.appSessionId,
          existingLessonVisitId: currentStore.lessonVisitId,
        });
      }

      if (!currentStore.appSessionId) {
        await startAppSession();
      }

      const resolvedStore = useAnalyticsStore.getState();
      const targetAppSessionId = resolvedStore.appSessionId;
      if (!targetAppSessionId) {
        if (analyticsDebugEnabled) {
          console.warn("[analytics] Missing appSessionId for lesson visit start");
        }
        return null;
      }

      // End any existing lesson visit first
      if (currentStore.lessonVisitId) {
        // Flush content time before ending
        currentStore.flushContentTime();
        currentStore.flushLessonActiveTime();

        // Send lesson active seconds, not app active seconds (Step 2)
        const updatedStore = useAnalyticsStore.getState();
        apiCall("/telemetry/lesson-visit/end", {
          lesson_visit_id: updatedStore.lessonVisitId,
          student_id: studentId,
          active_seconds: updatedStore.lessonActiveSeconds,
          video_time_seconds: updatedStore.videoTimeSeconds,
          quiz_time_seconds: updatedStore.quizTimeSeconds,
          coding_time_seconds: updatedStore.codingTimeSeconds,
        });
      }

      resetLessonTimes();
      setLessonVisitId(null);

      const result = await postAnalyticsJson<{ lesson_visit_id: number }>("/telemetry/lesson-visit/start", {
        student_id: studentId,
        app_session_id: targetAppSessionId,
        lesson_id: lessonId,
        unit_id: unitId,
        course_id: courseId,
        ...revisions,
      }, { keepalive: false });

      if (!result.ok && result.status === 409) {
        // A conflict can also mean a content revision mismatch. Only replace
        // the session when the server confirms this particular ID is inactive.
        const status = await apiCall<{ app_session_valid: boolean }>(
          "/telemetry/app-session/status",
          { student_id: studentId, app_session_id: targetAppSessionId }
        );
        const latestStore = useAnalyticsStore.getState();
        if (status?.app_session_valid === false &&
            latestStore.appSessionId === targetAppSessionId) {
          latestStore.resetSession();
        }
        // App's session initializer and the lesson retry establish fresh IDs.
        return null;
      }

      if (result.ok && result.data?.lesson_visit_id &&
          useAnalyticsStore.getState().appSessionId === targetAppSessionId &&
          !useAnalyticsStore.getState().isSigningOut) {
        setLessonVisitId(result.data.lesson_visit_id);
        return result.data.lesson_visit_id;
      }
      return null;
    },
    [studentId, isHydrated, resetLessonTimes, setLessonVisitId, analyticsDebugEnabled, startAppSession]
  );

  const endLessonVisit = useCallback(
    (useKeepalive = false) => {
      const currentStore = useAnalyticsStore.getState();
      if (!currentStore.lessonVisitId || !studentId) return;

      // Flush content time before ending
      currentStore.flushContentTime();
      currentStore.flushLessonActiveTime();

      const updatedStore = useAnalyticsStore.getState();
      if (analyticsDebugEnabled) {
        console.debug("[analytics] endLessonVisit", {
          lessonVisitId: updatedStore.lessonVisitId,
          currentContentType: updatedStore.currentContentType,
          activeSeconds: updatedStore.lessonActiveSeconds,
          videoTimeSeconds: updatedStore.videoTimeSeconds,
          quizTimeSeconds: updatedStore.quizTimeSeconds,
          codingTimeSeconds: updatedStore.codingTimeSeconds,
          useKeepalive,
        });
      }
      // Send lesson active seconds, not app active seconds (Step 2)
      apiCall(
        "/telemetry/lesson-visit/end",
        {
          lesson_visit_id: updatedStore.lessonVisitId,
          student_id: studentId,
          active_seconds: updatedStore.lessonActiveSeconds,
          video_time_seconds: updatedStore.videoTimeSeconds,
          quiz_time_seconds: updatedStore.quizTimeSeconds,
          coding_time_seconds: updatedStore.codingTimeSeconds,
        },
        useKeepalive
      );

      setLessonVisitId(null);
      resetLessonTimes();
    },
    [
      studentId,
      setLessonVisitId,
      resetLessonTimes,
      analyticsDebugEnabled,
    ]
  );

  // ----------------------------------------
  // Event Tracking
  // ----------------------------------------

  const trackEvent = useCallback(
    <T extends AnalyticsEventType>(
      eventType: T,
      eventData?: AnalyticsEventDataMap[T],
      options?: { lessonId?: number; unitId?: number; courseId?: number; recordingContext?: {app_session_id: number | null; lesson_visit_id: number | null} }
    ) => {
      if (!studentId) return;

      const currentStore = useAnalyticsStore.getState();
      const events: UserEvent[] = [
        {
          event_type: eventType,
          event_data: eventData,
          lesson_id: options?.lessonId,
          unit_id: options?.unitId,
          course_id: options?.courseId,
          timestamp: new Date().toISOString(),
        },
      ];

      sendAnalyticsEvents({
        student_id: studentId,
        app_session_id: options?.recordingContext ? options.recordingContext.app_session_id : currentStore.appSessionId,
        lesson_visit_id: options?.recordingContext ? options.recordingContext.lesson_visit_id : currentStore.lessonVisitId,
        events,
      });

    },
    [studentId]
  );

  const trackQuizQuestionView = useCallback(
    async (
      questionId: number,
      viewSecondsDelta?: number,
      options?: { isNewView?: boolean; updateId?: string }
    ) => {
      if (!studentId) return;

      const currentStore = useAnalyticsStore.getState();
      try {
        const payload: Record<string, unknown> = {
          student_id: studentId,
          question_id: questionId,
          update_id: options?.updateId,
          view_seconds_delta: viewSecondsDelta,
          is_new_view: options?.isNewView ?? true, // Default true for backward compatibility
        };
        if (currentStore.lessonVisitId) {
          payload.lesson_visit_id = currentStore.lessonVisitId;
        } else if (analyticsDebugEnabled) {
          console.debug("[analytics] quiz-question-view missing lessonVisitId", {
            questionId,
          });
        }

        await apiCall("/telemetry/quiz-question-view", payload);
      } catch (error) {
        console.warn("[analytics] Failed to record quiz question view", {
          questionId,
          viewSecondsDelta,
          error,
        });
      }
    },
    [studentId, analyticsDebugEnabled]
  );

  return {
    // Session management
    appSessionId,
    lessonVisitId,
    startAppSession,
    endAppSession,
    startLessonVisit,
    endLessonVisit,

    // Event tracking
    trackEvent,
    trackQuizQuestionView,

    // Content type tracking
    setCurrentContentType,
    setVideoPlaying,

    // State
    isTabVisible,
    isIdle,
  };
}

// ============================================================================
// Combined Analytics Hook (backward compatibility)
// ============================================================================

/**
 * useAnalytics
 * Combined hook for backward compatibility.
 * For new code, prefer using useAnalyticsLifecycle (once in App) + useAnalyticsActions (in components).
 *
 * WARNING: If multiple components call useAnalytics, they will each set up their own
 * heartbeat/idle detection. Use useAnalyticsActions in components and useAnalyticsLifecycle
 * only in App.tsx for proper singleton behavior.
 */
export function useAnalytics(studentId: string | undefined) {
  // Get actions
  const actions = useAnalyticsActions(studentId);

  // Note: For proper singleton behavior, useAnalyticsLifecycle should be called
  // separately in App.tsx. This combined hook is for backward compatibility.

  return actions;
}

export default useAnalytics;
